import React from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector';
import { useAreaFormPanel, type AreaFormRecord } from '@/hooks/organization/area/useAreaFormPanel';


interface AreaFormPanelProps {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: AreaFormRecord;
}

export const AreaFormPanel: React.FC<AreaFormPanelProps> = ({
  onSubmit,
  readOnly,
  record,
}) => {
  const { t: translate } = useTranslation();

  const {
    codeValue,
    nameValue,
    parentAreaValue,
    parentAreaOptions,
    metadata,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    handleCodeChange,
    handleNameChange,
    handleParentAreaChange,
    handleSubmit,
    errors,
    isSubmitted,
  } = useAreaFormPanel({ onSubmit, readOnly, record });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="area-form-panel">
      <SidePanelFormLayout.Fields>
        {isEditMode && metadata && (
          <LabelInput
            label={translate('organization:fields.id')}
            value={metadata.id}
            disabled
          />
        )}

        <LabelInput
          label={translate('organization:fields.areaName')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.name?.message && isSubmitted
              ? translate(String(errors.name.message))
              : undefined
          }
          data-testid="area-name-input"
        />

        <Selector
          label={translate('organization:fields.parentArea')}
          required
          options={parentAreaOptions}
          value={parentAreaValue}
          onChange={handleParentAreaChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.parentAreaId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.parentAreaId && isSubmitted
              ? translate(String(errors.parentAreaId.message))
              : undefined
          }
          data-testid="area-parent-selector"
        />

        <LabelInput
          label={translate('organization:fields.areaCode')}
          value={codeValue}
          onChange={handleCodeChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.code && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.code?.message && isSubmitted
              ? translate(String(errors.code.message))
              : undefined
          }
          data-testid="area-code-input"
        />

        {isEditMode && metadata && (
          <>
            <LabelInput
              label={translate('common:fields.createdDate')}
              value={metadata.createdDate}
              disabled
            />

            <LabelInput
              label={translate('common:fields.createdBy')}
              value={metadata.createdBy}
              disabled
            />

            <LabelInput
              label={translate('common:fields.modifiedDate')}
              value={metadata.modifiedDate}
              disabled
            />

            <LabelInput
              label={translate('common:fields.modifiedBy')}
              value={metadata.modifiedBy}
              disabled
            />
          </>
        )}
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard
            action={isEditMode ? 'edit' : 'create'}
            denied={!canSave}
          >
            <PrimaryButton type="submit" fullWidth isLoading={isSubmitting} disabled={!isReady || isSubmitting}>
              {translate(isEditMode ? 'common:actions.save' : 'organization:actions.registerArea')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default AreaFormPanel;
